<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="8" :sm="24">
              <a-form-item :label="$t('views.systemRole.query.roleName')">
                <a-input v-model="queryParam.roleName" :placeholder="$t('views.systemRole.query.roleNamePlaceholder')" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24">
              <a-form-item :label="$t('views.systemRole.query.roleKey')">
                <a-input v-model="queryParam.roleKey" :placeholder="$t('views.systemRole.query.roleKeyPlaceholder')" allow-clear />
              </a-form-item>
            </a-col>
            <template v-if="advanced">
              <a-col :md="8" :sm="24">
                <a-form-item :label="$t('views.systemRole.query.status')">
                  <a-select :placeholder="$t('views.systemRole.query.statusPlaceholder')" v-model="queryParam.status" style="width: 100%" allow-clear>
                    <a-select-option
                      v-for="(d, index) in localizedStatusOptions"
                      :key="index"
                      :value="d.value">{{ d.label }}</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :md="8" :sm="24">
                <a-form-item :label="$t('views.systemRole.query.createTime')">
                  <a-range-picker
                    style="width: 100%"
                    v-model="dateRange"
                    valueFormat="YYYY-MM-DD"
                    format="YYYY-MM-DD"
                    allow-clear />
                </a-form-item>
              </a-col>
            </template>
            <a-col :md="!advanced && 8 || 24" :sm="24">
              <span
                class="table-page-search-submitButtons"
                :style="advanced && { float: 'right', overflow: 'hidden' } || {}">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />{{ $t('views.systemRole.btn.query') }}</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />{{ $t('views.systemRole.btn.reset') }}</a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <div class="table-operations">
        <a-button type="primary" @click="$refs.createForm.handleAdd()" v-hasPermi="['system:role:add']">
          <a-icon type="plus" />{{ $t('views.systemRole.btn.add') }}
        </a-button>
        <a-button
          type="primary"
          :disabled="single"
          @click="$refs.createForm.handleUpdate(undefined, ids)"
          v-hasPermi="['system:role:edit']">
          <a-icon type="edit" />{{ $t('views.systemRole.btn.edit') }}
        </a-button>
        <a-button type="danger" :disabled="multiple" @click="handleDelete" v-hasPermi="['system:role:remove']">
          <a-icon type="delete" />{{ $t('views.systemRole.btn.delete') }}
        </a-button>
        <table-setting
          :style="{ float: 'right' }"
          :table-size.sync="tableSize"
          v-model="columns"
          :refresh-loading="loading"
          @refresh="getList" />
      </div>
      <create-form ref="createForm" :statusOptions="dict.type['sys_enable_disable']" @ok="getList" />
      <create-data-scope-form ref="createDataScopeForm" @ok="getList" />
      <create-mask-scope-form ref="createMaskScopeForm" @ok="getList" />
      <select-user
        ref="selectUser"
        :statusOptions="dict.type['sys_normal_disable']"
      />
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="displayColumns"
        :data-source="list"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :pagination="false"
        :bordered="tableBordered">
        <a-switch slot="status" slot-scope="text, record" @change="changeStatus($event,record)" :checked="record.status === 1 " />
        <span slot="createTime" slot-scope="text, record">
          {{ parseTime(record.createTime) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a @click="$refs.createForm.handleUpdate(record, undefined)" v-hasPermi="['system:role:edit']">
            <a-icon type="edit" />
            {{ $t('views.systemRole.btn.edit') }}
          </a>
          <a-divider type="vertical" v-hasPermi="['system:role:remove']" />
          <a @click="handleDelete(record)" v-hasPermi="['system:role:remove']">
            <a-icon type="delete" />
            {{ $t('views.systemRole.btn.delete') }}
          </a>
          <a-divider type="vertical" v-hasPermi="['system:role:edit']" />
          <a-dropdown v-hasPermi="['system:role:edit']">
            <a class="ant-dropdown-link" @click="e => e.preventDefault()">
              <a-icon type="double-right" />
              {{ $t('views.systemRole.btn.more') }}
            </a>
            <a-menu slot="overlay">
              <a-menu-item>
                <a @click="handleAuthUser(record)">
                  <a-icon type="user-add" />
                  {{ $t('views.systemRole.action.assignUser') }}
                </a>
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </span>
      </a-table>
      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="showPaginationTotal"
        @showSizeChange="onShowSizeChange"
        @change="changeSize" />
    </a-card>
  </page-header-wrapper>
</template>

<script>

import { pageRole, delRole, changeRoleStatus } from '@/api/system/role'
import CreateForm from './modules/CreateForm'
import CreateDataScopeForm from './modules/CreateDataScopeForm'
import CreateMaskScopeForm from './modules/CreateMaskScopeForm'
import SelectUser from './modules/SelectUser'
import { tableMixin } from '@/store/table-mixin'
import { getMaskRoleData } from '@/api/system/mask'

export default {
  name: 'Role',
  components: {
    CreateForm,
    CreateDataScopeForm,
    CreateMaskScopeForm,
    SelectUser
  },
  mixins: [tableMixin],
  dicts: ['sys_enable_disable', 'sys_normal_disable'],
  data() {
    return {
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      advanced: false,
      single: true,
      multiple: true,
      ids: [],
      loading: false,
      total: 0,
      dateRange: [],
      queryParam: {
        pageNum: 1,
        pageSize: 10,
        roleName: undefined,
        roleKey: undefined,
        status: undefined
      },
      columns: [
        {
          title: '',
          dataIndex: 'id',
          align: 'center'
        },
        {
          title: '',
          dataIndex: 'roleName',
          ellipsis: true,
          align: 'center'
        },
        {
          title: '',
          dataIndex: 'roleKey',
          ellipsis: true,
          align: 'center'
        },
        {
          title: '',
          dataIndex: 'roleSort',
          align: 'center'
        },
        {
          title: '',
          dataIndex: 'status',
          scopedSlots: { customRender: 'status' },
          align: 'center'
        },
        {
          title: '',
          dataIndex: 'createTime',
          scopedSlots: { customRender: 'createTime' },
          align: 'center'
        },
        {
          title: '',
          dataIndex: 'operation',
          width: '20%',
          scopedSlots: { customRender: 'operation' },
          align: 'center'
        }
      ]
    }
  },
  created() {
    this.getList()
  },
  computed: {
    localizedStatusOptions () {
      return this.localizeDictOptions(this.dict.type['sys_enable_disable'], 'views.systemRole.dict.status')
    },
    displayColumns () {
      const titleKeyByDataIndex = {
        id: 'views.systemRole.column.id',
        roleName: 'views.systemRole.column.roleName',
        roleKey: 'views.systemRole.column.roleKey',
        roleSort: 'views.systemRole.column.roleSort',
        status: 'views.systemRole.column.status',
        createTime: 'views.systemRole.column.createTime',
        operation: 'views.systemRole.column.operation'
      }
      return this.columns.map(col => {
        const key = titleKeyByDataIndex[col.dataIndex]
        return {
          ...col,
          title: key ? this.$t(key) : col.title
        }
      })
    }
  },
  methods: {
    localizeDictOptions (options, keyPrefix) {
      return (options || []).map(item => {
        const i18nKey = `${keyPrefix}.${item.value}`
        return {
          ...item,
          label: this.$te(i18nKey) ? this.$t(i18nKey) : item.label
        }
      })
    },
    showPaginationTotal (total) {
      return this.$t('views.systemRole.pagination.total', { total })
    },
    changeStatus(e, record) {
      this.confirmHandleStatus(record)
    },
    getList() {
      this.loading = true
      pageRole(this.addDateRange(this.queryParam, this.dateRange)).then(response => {
        this.list = response.data.records
        this.total = response.data.total
        this.loading = false
      })
    },
    handleQuery() {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery() {
      this.dateRange = []
      this.queryParam = {
        pageNum: 1,
        pageSize: 10,
        roleName: undefined,
        roleKey: undefined,
        status: undefined
      }
      this.handleQuery()
    },
    onShowSizeChange(current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    changeSize(current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    onSelectChange(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.ids = this.selectedRows.map(item => item.id)
      this.single = selectedRowKeys.length !== 1
      this.multiple = !selectedRowKeys.length
    },
    confirmHandleStatus(row) {
      const oldStatus = row.status
      const action = row.status === 0
        ? this.$t('views.systemRole.status.enable')
        : this.$t('views.systemRole.status.disable')
      const status = row.status === 1 ? 0 : 1
      changeRoleStatus(row.id, status)
        .then((res) => {
          if (res.success) {
            row.status = status
            this.$message.success(this.$t('views.systemRole.msg.statusSuccess', { action }), 3)
          } else {
            row.status = oldStatus
            this.$message.error(this.$t('views.systemRole.msg.statusFail', { action, message: res.message }), 3)
          }
        }).catch(() => {
          this.$message.error(this.$t('views.systemRole.msg.statusException', { action }), 3)
        })
    },
    handleDelete(row) {
      const that = this
      const roleIds = row.id || this.ids
      this.$confirm({
        title: this.$t('views.systemRole.confirm.deleteTitle'),
        content: this.$t('views.systemRole.confirm.deleteContent', { ids: roleIds }),
        onOk() {
          return delRole(roleIds)
            .then((res) => {
              if (res.code == 1 || res.code == 20001) {
                that.onSelectChange([], [])
                that.getList()
                that.$message.success(that.$t('views.systemRole.msg.deleteSuccess'), 3)
              } else {
                that.$message.error(res.message, 3)
              }
            })
        },
        onCancel() { }
      })
    },
    handleMaskData(row) {
      const roleIds = row.id || this.ids
      this.maskRoleId = roleIds
      this.maskRoleDateVisible = true
      getMaskRoleData(row.id).then(response => {
        this.tableData = response.data
      })
    },
    handleExport() {
      const that = this
      this.$confirm({
        title: this.$t('views.systemRole.confirm.exportTitle'),
        content: this.$t('views.systemRole.confirm.exportContent'),
        onOk() {
          that.download('system/role/export', {
            ...that.queryParam
          }, `role_${new Date().getTime()}.xlsx`)
        },
        onCancel() { }
      })
    },
    handleAuthUser (row) {
      this.$refs.selectUser.handleAuthUser(row)
    }
  }
}
</script>
